import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import EvaluationPeriodPicker from "../../../components/filters/EvaluationPeriodPicker";
import PaginationFooter from "../../../components/common/PaginationFooter";
import { movementTypeLabel } from "../../../utils/i18nLabels";

const PAGE_SIZE_OPTIONS = Object.freeze([25, 50, 100]);

function resultRange(pagination) {
  const total = Number(pagination?.total || 0);
  const page = Number(pagination?.page || 1);
  const pageSize = Number(pagination?.page_size || 25);
  return { total, from: total ? (page - 1) * pageSize + 1 : 0, to: Math.min(page * pageSize, total) };
}

function formatDecimal(value, digits = 3) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(digits).replace(/\.?0+$/, "");
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)} PLN` : String(value);
}

function formatDateOnly(value, language = "en") {
  if (!value) return "—";
  const raw = String(value);
  const direct = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  const d = new Date(direct ? `${direct[1]}T00:00:00` : value);
  if (Number.isNaN(d.getTime())) return raw;
  const locale = String(language || "en").startsWith("pl") ? "pl-PL" : "en-GB";
  return d.toLocaleDateString(locale, { year: "numeric", month: "2-digit", day: "2-digit" });
}

function unitOf(product) {
  return product?.stockUnit?.symbol || product?.stock_unit_code || "pcs";
}

function movementDirection(row) {
  const hasFrom = row?.from_location_id !== null && row?.from_location_id !== undefined;
  const hasTo = row?.to_location_id !== null && row?.to_location_id !== undefined;
  if (hasFrom && hasTo) return { kind: "transfer", prefix: "↔" };
  if (hasTo && !hasFrom) return { kind: "positive", prefix: "+" };
  if (hasFrom && !hasTo) return { kind: "negative", prefix: "−" };
  const delta = Number(row?.product_quantity_delta);
  if (Number.isFinite(delta) && delta > 0) return { kind: "positive", prefix: "+" };
  if (Number.isFinite(delta) && delta < 0) return { kind: "negative", prefix: "−" };
  return { kind: "neutral", prefix: "" };
}

function movementBatchSummary(row) {
  const allocations = Array.isArray(row?.batchAllocations) ? row.batchAllocations : [];
  if (!allocations.length) return "—";
  const labels = allocations.slice(0, 2).map((allocation) => allocation.batch?.batch_number || `#${allocation.batch_id}`);
  if (allocations.length > 2) labels.push(`+${allocations.length - 2}`);
  return labels.join(" · ");
}

function StockProductWorkspace({
  product,
  readContext = null,
  contextLoading = false,
  contextError = "",
  onRetryContext,
  batchOptionsLimited = false,
  canPost = false,
  postingActions = [],
  onPost,
  masterLocationName = "",
  locations = [],
  batchRows = [],
  batchLoading = false,
  batchPagination = null,
  batchPageSize = 25,
  onBatchPageChange,
  onBatchPageSizeChange,
  batchLocationId = "",
  onBatchLocationChange,
  onShowAllBatches,
  onTraceBatch,
  movementRows = [],
  movementLoading = false,
  movementPagination = null,
  movementPageSize = 25,
  onMovementPageChange,
  onMovementPageSizeChange,
  movementPeriod,
  movementType = "",
  movementTypes = [],
  onMovementTypeChange,
  movementBatchId = "",
  movementBatchOptions = [],
  onMovementBatchChange,
  onClearMovementFilters,
  onShowAllMovements,
  onOpenMovement,
}) {
  const { t, i18n } = useTranslation(["common", "stock"]);
  const language = i18n.resolvedLanguage || i18n.language;
  const unit = unitOf(product);
  const batchTableRef = useRef(null);
  const movementTableRef = useRef(null);
  const batchRange = resultRange(batchPagination);
  const movementRange = resultRange(movementPagination);
  // Scroll the newly selected page to its first row, not the entire document.
  useEffect(() => {
    if (batchTableRef.current) batchTableRef.current.scrollTop = 0;
  }, [product?.id, batchPagination?.page, batchPagination?.page_size, batchLocationId]);
  useEffect(() => {
    if (movementTableRef.current) movementTableRef.current.scrollTop = 0;
  }, [product?.id, movementPagination?.page, movementPagination?.page_size,
    movementPeriod?.from, movementPeriod?.to, movementType, movementBatchId]);

  const positiveLocations = useMemo(() => {
    const rows = Array.isArray(product?.available_locations) ? product.available_locations : [];
    return rows.filter((row) => Number(row.quantity || 0) !== 0);
  }, [product]);

  if (!product) {
    return (
      <section className="stock-product-detail stock-product-detail--empty">
        <strong>{t(contextError ? "stock:workspace.contextReadFailed" : contextLoading ? "common:common.loading" : "stock:workspace.selectProductTitle")}</strong>
        {contextError ? (
          <div role="alert"><p>{contextError}</p><button type="button" className="stock-btn stock-btn--outline" onClick={onRetryContext}>{t("stock:workspace.retryRead")}</button></div>
        ) : <span>{t("stock:workspace.selectProductHint")}</span>}
      </section>
    );
  }

  return (
    <section className="stock-product-detail" aria-label={t("stock:workspace.detailAriaLabel", { product: product.name_public || "" })}>
      <div className="stock-workspace-read-status" aria-live="polite">
        {contextLoading ? t("stock:workspace.refreshingContext") : null}
        {readContext && !contextLoading && !contextError ? <p className="stock-read-context">{t("stock:integration.readSnapshot")}: {readContext.request_started_at} · {readContext.id}</p> : null}
        {contextError ? <div role="alert"><strong>{t("stock:workspace.contextReadFailed")}</strong><p>{contextError}</p><button type="button" className="stock-btn stock-btn--outline" onClick={onRetryContext}>{t("stock:workspace.retryRead")}</button></div> : null}
      </div>
      {batchOptionsLimited && <p>{t("stock:integration.batchOptionsLimited")}</p>}
      <div className="stock-product-context">
        <div className="stock-product-context__identity">
          <span className="stock-product-context__eyebrow">{t("stock:workspace.activeProduct")}</span>
          <h2>{product.name_public || `#${product.id}`}</h2>
          <p>
            {[product.variant_label, product.sku ? `SKU: ${product.sku}` : null, product.brand].filter(Boolean).join(" · ")}
          </p>
          {masterLocationName ? (
            <small>{t("stock:workspace.masterLocationContext", { location: masterLocationName })}</small>
          ) : null}
        </div>

        <div className="stock-product-context__metrics">
          <div>
            <span>{t("stock:workspace.totalQuantity")}</span>
            <strong>{formatDecimal(product.global_stock_quantity, 3)} {unit}</strong>
          </div>
          <div>
            <span>{t("stock:overview.movingAverageCost")}</span>
            <strong>{formatDecimal(product.average_cost_net, 4)} PLN/{unit}</strong>
          </div>
          <div>
            <span>{t("stock:overview.stockValue")}</span>
            <strong>{formatMoney(product.global_stock_value_net)}</strong>
          </div>
          <div>
            <span>{t("stock:workspace.locationCount")}</span>
            <strong>{product.stock_location_count ?? positiveLocations.length}</strong>
          </div>
        </div>

        {canPost ? (
          <div className="stock-product-context__actions" aria-label={t("stock:workspace.productActions")}>
            {postingActions.map((item) => (
              <button
                key={item.value}
                type="button"
                className={item.value === "receive" ? "stock-btn stock-btn--primary" : "stock-btn stock-btn--outline"}
                onClick={() => onPost?.(item.value)}
                disabled={contextLoading || Boolean(contextError)}
              >
                {item.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="stock-product-context__readonly">
            <strong>{t("stock:overview.readOnlyTitle")}</strong>
            <span>{t("stock:overview.readOnlyHint")}</span>
          </div>
        )}
      </div>

      <div className="stock-product-locations" aria-label={t("stock:workspace.locationsTitle")}>
        <div className="stock-product-section-heading">
          <div>
            <span>{t("stock:workspace.stockContextEyebrow")}</span>
            <h3>{t("stock:workspace.locationsTitle")}</h3>
          </div>
        </div>

        {positiveLocations.length ? (
          <div className="stock-product-location-grid">
            {positiveLocations.map((row) => (
              <div className="stock-product-location-card" key={row.id || `${row.product_id}-${row.location_id}`}>
                <span>{row.location?.name || `#${row.location_id}`}</span>
                <strong>{formatDecimal(row.quantity, 3)} {unit}</strong>
                <small>{formatMoney(row.stock_value_net)}</small>
              </div>
            ))}
          </div>
        ) : (
          <div className="stock-workspace-empty">{t("stock:workspace.noLocationStock")}</div>
        )}
      </div>

      <div className="stock-product-preview-section">
        <div className="stock-product-section-heading stock-product-section-heading--actions">
          <div>
            <span>{t("stock:workspace.batchEyebrow")}</span>
            <h3 id="stock-workspace-batches-heading">{t("stock:workspace.batchTitle")}</h3>
            <p className="stock-workspace-results-count" aria-live="polite">
              {batchLoading ? t("common:common.loading") : batchPagination && !contextError ? t("stock:workspace.batchResults", { total: batchRange.total }) : "—"}
            </p>
          </div>
          <div className="stock-product-section-heading__actions">
            <label>
              <span>{t("stock:overview.location")}</span>
              <select value={batchLocationId} onChange={(event) => onBatchLocationChange?.(event.target.value)} disabled={batchLoading}>
                <option value="">{t("stock:overview.allLocations")}</option>
                {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
              </select>
            </label>
            <button type="button" className="stock-btn stock-btn--outline" onClick={onShowAllBatches} disabled={contextLoading || Boolean(contextError)}>
              {t("stock:workspace.showAllBatches")}
            </button>
          </div>
        </div>

        <div ref={batchTableRef} className="stock-table-wrap stock-workspace-table-wrap" aria-busy={batchLoading}>
          <table className="stock-table stock-workspace-table" aria-labelledby="stock-workspace-batches-heading">
            <thead>
              <tr>
                <th>{t("stock:batches.batchNumber")}</th>
                <th>{t("stock:batches.expiryDate")}</th>
                <th>{t("stock:overview.location")}</th>
                <th className="num">{t("stock:overview.quantity")}</th>
                <th className="num">{t("stock:batches.batchAverageCost")}</th>
                <th>{t("stock:overview.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {!batchLoading && batchRows.length === 0 ? (
                <tr><td colSpan="6" className="stock-empty">{t(contextError ? "stock:workspace.contextReadFailed" : "stock:workspace.noBatches")}</td></tr>
              ) : null}
              {batchLoading && batchRows.length === 0 ? (
                <tr><td colSpan="6" className="stock-empty">{t("common:common.loading")}</td></tr>
              ) : null}
              {batchRows.map((row) => (
                <tr key={row.id}>
                  <td><strong>{row.batch?.batch_number || "—"}</strong></td>
                  <td>{formatDateOnly(row.batch?.expiry_date, language)}</td>
                  <td>{row.location?.name || "—"}</td>
                  <td className="num">{formatDecimal(row.quantity, 3)} {row.stock_unit_code || unit}</td>
                  <td className="num">
                    {row.product?.stock_valuation_method === "batch_moving_average" && row.batch?.cost?.average_cost_net != null
                      ? `${formatDecimal(row.batch.cost.average_cost_net, 4)} PLN/${row.stock_unit_code || unit}`
                      : "—"}
                  </td>
                  <td>
                    {row.batch?.id ? (
                      <button type="button" className="stock-link-btn" onClick={() => onTraceBatch?.(row.batch)}>
                        {t("stock:workspace.showBatchMovements")}
                      </button>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationFooter
          className="stock-workspace-pagination"
          page={batchPagination?.page || 1}
          pageCount={batchPagination?.page_count || 1}
          pageSize={batchPagination?.page_size || batchPageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          totalRows={batchRange.total}
          disabled={batchLoading || Boolean(contextError) || !batchPagination}
          summary={<span>{batchLoading ? t("common:common.loading") : batchPagination && !contextError ? t("stock:workspace.resultsRange", batchRange) : "—"}</span>}
          onPageSizeChange={onBatchPageSizeChange}
          onPrev={() => onBatchPageChange?.(Math.max(1, Number(batchPagination?.page || 1) - 1))}
          onNext={() => onBatchPageChange?.(Math.min(Number(batchPagination?.page_count || 1), Number(batchPagination?.page || 1) + 1))}
        />
      </div>

      <div id="stock-product-workspace-movements" className="stock-product-preview-section stock-product-preview-section--movements">
        <div className="stock-product-section-heading stock-product-section-heading--actions">
          <div>
            <span>{t("stock:workspace.movementEyebrow")}</span>
            <h3 id="stock-workspace-movements-heading">{t("stock:workspace.movementTitle")}</h3>
            <p className="stock-workspace-results-count" aria-live="polite">
              {movementLoading ? t("common:common.loading") : movementPagination && !contextError ? t("stock:workspace.movementResults", { total: movementRange.total }) : "—"}
            </p>
          </div>
          <button type="button" className="stock-btn stock-btn--outline" onClick={onShowAllMovements} disabled={contextLoading || Boolean(contextError)}>
            {t("stock:workspace.showFullHistory")}
          </button>
        </div>

        <div className="stock-workspace-movement-filters">
          <div className="stock-workspace-movement-filters__period">
            <EvaluationPeriodPicker
              controller={movementPeriod}
              disabled={movementLoading}
              className="stock-workspace-period"
              modeLabel={t("stock:audit.periodMode")}
              displayLabel={t("stock:audit.period")}
              fromLabel={t("stock:audit.periodFrom")}
              toLabel={t("stock:audit.periodTo")}
            />
          </div>
          <label>
            <span>{t("stock:audit.movementTypeFilter")}</span>
            <select value={movementType} onChange={(event) => onMovementTypeChange?.(event.target.value)} disabled={movementLoading}>
              <option value="">{t("stock:audit.allMovementTypes")}</option>
              {movementTypes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label>
            <span>{t("stock:workspace.lotFilter")}</span>
            <select value={movementBatchId} onChange={(event) => onMovementBatchChange?.(event.target.value)} disabled={movementLoading}>
              <option value="">{t("stock:workspace.allLots")}</option>
              {movementBatchOptions.map((batch) => (
                <option key={batch.id} value={batch.id}>{batch.batch_number || `#${batch.id}`}</option>
              ))}
            </select>
          </label>
          <button type="button" className="stock-link-btn stock-workspace-clear" onClick={onClearMovementFilters} disabled={movementLoading}>
            {t("stock:audit.clearFilters")}
          </button>
        </div>

        <div ref={movementTableRef} className="stock-table-wrap stock-workspace-table-wrap" aria-busy={movementLoading}>
          <table className="stock-table stock-workspace-table stock-workspace-movement-table" aria-labelledby="stock-workspace-movements-heading">
            <thead>
              <tr>
                <th>{t("stock:audit.businessDate")}</th>
                <th>{t("stock:audit.movementType")}</th>
                <th>{t("stock:audit.route")}</th>
                <th className="num">{t("stock:audit.change")}</th>
                <th className="num">{t("stock:audit.balanceAfter")}</th>
                <th className="num">{t("stock:audit.averageCostAfter")}</th>
                <th>{t("stock:audit.batch")}</th>
              </tr>
            </thead>
            <tbody>
              {!movementLoading && movementRows.length === 0 ? (
                <tr><td colSpan="7" className="stock-empty">{t(contextError ? "stock:workspace.contextReadFailed" : "stock:workspace.noMovements")}</td></tr>
              ) : null}
              {movementLoading && movementRows.length === 0 ? (
                <tr><td colSpan="7" className="stock-empty">{t("common:common.loading")}</td></tr>
              ) : null}
              {movementRows.map((row) => {
                const direction = movementDirection(row);
                const movementUnit = row.stock_unit_code || unit;
                const selectedBatch = movementBatchId
                  ? (row.batchAllocations || []).find((allocation) => String(allocation.batch_id) === String(movementBatchId))
                  : null;
                const displayQuantity = movementBatchId && row.trace_batch_quantity != null
                  ? row.trace_batch_quantity
                  : row.quantity;
                const balanceAfter = movementBatchId && row.trace_batch_quantity_after != null
                  ? row.trace_batch_quantity_after
                  : row.product_quantity_after;

                return (
                  <tr
                    key={row.id}
                    className="stock-audit-row"
                    tabIndex="0"
                    role="button"
                    onClick={() => onOpenMovement?.(row)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onOpenMovement?.(row);
                      }
                    }}
                  >
                    <td><strong>{formatDateOnly(row.business_date, language)}</strong></td>
                    <td>{movementTypeLabel(t, row.movement_type)}</td>
                    <td className="stock-audit-route"><span>{row.fromLocation?.name || "—"}</span><strong>→</strong><span>{row.toLocation?.name || "—"}</span></td>
                    <td className="num"><strong className={`stock-ledger-change stock-ledger-change--${direction.kind}`}>{direction.prefix}{formatDecimal(displayQuantity, 3)} {movementUnit}</strong></td>
                    <td className="num"><strong>{formatDecimal(balanceAfter, 3)} {movementUnit}</strong></td>
                    <td className="num">{row.average_cost_after_net == null ? "—" : `${formatDecimal(row.average_cost_after_net, 4)} PLN/${movementUnit}`}</td>
                    <td>{selectedBatch?.batch?.batch_number || movementBatchSummary(row)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <PaginationFooter
          className="stock-workspace-pagination"
          page={movementPagination?.page || 1}
          pageCount={movementPagination?.page_count || 1}
          pageSize={movementPagination?.page_size || movementPageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          totalRows={movementRange.total}
          disabled={movementLoading || Boolean(contextError) || !movementPagination}
          summary={<span>{movementLoading ? t("common:common.loading") : movementPagination && !contextError ? t("stock:workspace.resultsRange", movementRange) : "—"}</span>}
          onPageSizeChange={onMovementPageSizeChange}
          onPrev={() => onMovementPageChange?.(Math.max(1, Number(movementPagination?.page || 1) - 1))}
          onNext={() => onMovementPageChange?.(Math.min(Number(movementPagination?.page_count || 1), Number(movementPagination?.page || 1) + 1))}
        />
      </div>
    </section>
  );
}

export default StockProductWorkspace;
